import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { tgIcon, vkIcon } from "../../../images";
import nintendoSearchService from "../../service/nintendoSearch.service";
import PsPricesDealsPage from "../../pages/pspricesDealsPage";
import NintendoGameSearch from "../../components/nintendoDeals/NintendoGameSearch";
import {
  CARD_REGION,
  useNintendoCards,
} from "../../components/nintendoDeals/useNintendoCards";
import { pickCards, rubAtCardRate } from "../../utils/pickCards";

const REGIONS = [
  { value: "us", label: "США", primary: true },
  { value: "pl", label: "Польша", primary: true },
  { value: "fr", label: "Европа" },
  { value: "br", label: "Бразилия" },
  { value: "ca", label: "Канада" },
  { value: "hk", label: "Гонконг" },
  { value: "se", label: "Швеция" },
];

const BUY_NOT_FOUND =
  "Игра по ссылке не найдена. Найдите её через поиск ниже — подберём карты под цену.";
const BUY_UNAVAILABLE =
  "Не удалось загрузить цену игры по ссылке. Попробуйте позже или найдите игру через поиск ниже.";

const pillClass =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-medium shadow-sm transition";
const outlinePill = `${pillClass} border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:text-indigo-700 dark:border-gray-600 dark:bg-[#2a2a2a] dark:text-gray-200`;

function getBuyOnSiteUrl(region) {
  if (region === "us" || region === "pl") {
    return "https://trendy-eng-shop.ru/nintendo-eshop/shop";
  }
  return "https://trendy-eng-shop.ru/store/nintendo";
}

function BuyLinks({ region }) {
  return (
    <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-center gap-2 px-1 text-sm">
      <span className="basis-full text-center text-gray-500 dark:text-gray-400 sm:mr-1 sm:basis-auto">
        Коды пополнения:
      </span>
      <a
        href={getBuyOnSiteUrl(region)}
        className={`${pillClass} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-500`}
        target="_blank"
        rel="noreferrer"
      >
        На сайте
      </a>
      <a
        href="https://t.me/trendy_eng_shop_bot?start=ref-001"
        className={outlinePill}
        target="_blank"
        rel="noreferrer"
        onClick={() => window.ym?.(93997310, "reachGoal", "tgClickNintendo")}
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2AABEE]">
          <img src={tgIcon} alt="" className="h-3 w-3" />
        </span>
        Telegram
      </a>
      <a
        href="https://vk.com/trendy_eng_shop"
        className={outlinePill}
        target="_blank"
        rel="noreferrer"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0077FF]">
          <img src={vkIcon} alt="" className="h-3 w-3" />
        </span>
        ВКонтакте
      </a>
      <a
        href="/nintendo/igry"
        className="mt-1 basis-full text-center font-medium text-indigo-600 hover:underline dark:text-indigo-400 sm:ml-1 sm:mt-0 sm:basis-auto"
      >
        Все игры с ценами в рублях →
      </a>
    </div>
  );
}

/** Покупка кода под игру из витрины: { game, context } для карточки игры или null. */
function buildDealPick(deal, region, cardsFor) {
  const cardRegion = CARD_REGION[region];
  const catalog = cardsFor(cardRegion);
  if (!catalog || deal.amount == null || deal.currency !== catalog.currency) {
    return null;
  }
  const plan = pickCards(deal.amount, catalog.denominations);
  if (!plan) return null;
  return {
    game: {
      nsuid: deal.nsuid,
      title: deal.name,
      image: deal.cover,
      price: deal.amount,
      original: deal.originalAmount,
      percent: deal.discountPercent,
      endsAt: deal.discountEndDate,
      seoPath: deal.seoPath,
      rub: rubAtCardRate(deal.amount, catalog.denominations) ?? plan.rub,
    },
    context: {
      region,
      currency: catalog.currency,
      label: REGIONS.find((item) => item.value === region)?.label,
      cardRegion,
      cards: catalog,
    },
  };
}

/** Покупка по ссылке со страницы игры: ответ /nintendo/offer → { game, context }. */
function buildOfferPick({ game, offer }, cardsFor) {
  const region = offer?.region;
  const cardRegion = CARD_REGION[region];
  return {
    game: {
      nsuid: offer?.nsuid,
      title: game.title,
      image: game.image,
      switch2: game.switch2,
      seoPath: game.seoPath,
      price: offer?.price ?? null,
      original: offer?.original ?? null,
      percent: offer?.percent ?? null,
      endsAt: offer?.endsAt ?? null,
      rub: offer?.rub ?? null,
    },
    context: {
      region,
      currency: offer?.currency,
      label: REGIONS.find((item) => item.value === region)?.label,
      cardRegion,
      cards: cardsFor(cardRegion),
    },
    openCheckout: true,
  };
}

function DealBuy({ pick, onBuy }) {
  return (
    <div className="flex items-center justify-between gap-1 pb-1">
      <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
        ≈ {Math.round(pick.game.rub).toLocaleString("ru-RU")} ₽
      </span>
      <button
        type="button"
        onClick={onBuy}
        className="shrink-0 whitespace-nowrap rounded bg-indigo-600 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-white hover:bg-indigo-500"
      >
        Купить код
      </button>
    </div>
  );
}

function NintendoDiscount() {
  const cards = useNintendoCards();
  const { cardsFor } = cards;
  const [dealPick, setDealPick] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const buySlug = searchParams.get("buy");
  const buyRegion = searchParams.get("region");
  const [buyOffer, setBuyOffer] = useState(null);
  const [buyError, setBuyError] = useState("");

  useEffect(() => {
    if (!buySlug) return undefined;
    const controller = new AbortController();
    setBuyError("");
    nintendoSearchService
      .offer(buySlug, buyRegion, controller.signal)
      .then(setBuyOffer)
      .catch((error) => {
        if (error.name === "AbortError") return;
        setBuyError(error.status === 404 ? BUY_NOT_FOUND : BUY_UNAVAILABLE);
        setSearchParams({}, { replace: true });
      });
    return () => controller.abort();
  }, [buySlug, buyRegion, setSearchParams]);

  useEffect(() => {
    if (!buyOffer || !(cards.loaded || cards.error)) return;
    if (buyOffer.game) {
      setDealPick(buildOfferPick(buyOffer, cardsFor));
    } else {
      setBuyError(BUY_NOT_FOUND);
    }
    setBuyOffer(null);
    setSearchParams({}, { replace: true });
  }, [buyOffer, cards.loaded, cards.error, cardsFor, setSearchParams]);

  const pickFor = useMemo(() => {
    const cache = new Map();
    return (deal, region) => {
      const key = `${region}:${deal.id}`;
      if (!cache.has(key)) cache.set(key, buildDealPick(deal, region, cardsFor));
      return cache.get(key);
    };
  }, [cardsFor]);

  const buyAction = (deal, region) => {
    const pick = pickFor(deal, region);
    return pick ? () => setDealPick({ ...pick }) : null;
  };

  return (
    <PsPricesDealsPage
      title="Скидки Nintendo Switch"
      platforms="Switch,Switch2"
      platformQuery="Switch,Switch2"
      regions={REGIONS}
      initialRegion={buyRegion}
      ymPath="/nintendo-discount"
      getDealImageAction={buyAction}
      moreActions={
        <a
          href="/nintendo/igry"
          className="inline-flex items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-400 dark:border-indigo-700 dark:bg-[#2a2a2a] dark:text-indigo-300"
        >
          Все игры с ценами в рублях →
        </a>
      }
      renderDealAction={(deal, region) => {
        const pick = pickFor(deal, region);
        return pick ? <DealBuy pick={pick} onBuy={() => setDealPick({ ...pick })} /> : null;
      }}
      renderTop={({ region, regions, onRegionChange }) => (
        <>
          <p className="-mt-1 text-center text-sm text-gray-500 dark:text-gray-400 md:-mt-3">
            Цены eShop со скидками и в рублях — по курсу наших карт пополнения
          </p>
          {buyError ? (
            <div
              role="alert"
              className="mx-auto mt-4 flex max-w-3xl items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
            >
              <span className="flex-1">{buyError}</span>
              <button
                type="button"
                onClick={() => setBuyError("")}
                aria-label="Закрыть"
                className="shrink-0 px-1 text-amber-700 hover:text-amber-900 dark:text-amber-300"
              >
                ×
              </button>
            </div>
          ) : null}
          <NintendoGameSearch
            region={region}
            regions={regions}
            onRegionChange={onRegionChange}
            cards={cards}
            externalPick={dealPick}
          />
          <BuyLinks region={region} />
          <h2 className="mx-4 mt-5 text-center text-lg font-semibold dark:text-white md:mt-6">
            Популярные скидки
          </h2>
        </>
      )}
    />
  );
}

export default NintendoDiscount;
